import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  TextField,
  Checkbox,
  Autocomplete,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { ICreateUserRequest } from '../../types/api/request/adminusersrequest';
import { IRootState } from '../../types/store';
import { AdminUsersService } from '../../api/services/adminUsersService';
import { addUser } from '../../store/Reducers/AdminUsersSlice';
import { BackButton } from '../../components/Buttons/BackButton';
import { ACCOUNT_MANAGEMENT_ROUTE } from '../../constants/routes';

export const CreateUserPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { content: roles } = useSelector((state: IRootState) => state.rolesAndPermissions);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ICreateUserRequest>({
    defaultValues: {
      username: '',
      password: '',
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      role_ids: [],
    },
    mode: 'onChange',
  });

  const onSubmit = async (payload: ICreateUserRequest) => {
    try {
      const user = await AdminUsersService.createUser(payload);
      dispatch(addUser(user));
      dispatch(
        showNotification({
          message: 'User successfully created',
        }),
      );
      navigate(ACCOUNT_MANAGEMENT_ROUTE); // redirect back to user list
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error?.response?.data?.message || 'Failed to create user',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant="h6" fontWeight={600} mb={2}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <BackButton onClick={() => navigate(ACCOUNT_MANAGEMENT_ROUTE)} />
          <span style={{ fontSize: 12 }}>Back to Users</span>
        </div>
        Create New User
      </Typography>

      <Paper
        elevation={1}
        sx={{
          maxWidth: 900,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr',
            },
            columnGap: 3,
            rowGap: 2.5,
          }}
        >
          {/* Username */}
          <FormControl fullWidth>
            <FormLabel>Username</FormLabel>
            <TextField
              size="small"
              placeholder="newadmin"
              error={!!errors.username}
              helperText={errors.username?.message}
              {...register('username', { required: 'Username is required' })}
            />
          </FormControl>

          {/* Password */}
          <FormControl fullWidth>
            <FormLabel>Password</FormLabel>
            <TextField
              size="small"
              type="password"
              placeholder="SecurePass123"
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register('password', { required: 'Password is required' })}
            />
          </FormControl>

          {/* Email */}
          <FormControl fullWidth>
            <FormLabel>Email</FormLabel>
            <TextField
              size="small"
              type="email"
              placeholder="admin@example.com"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email', { required: 'Email is required' })}
            />
          </FormControl>

          {/* Phone Number */}
          <FormControl fullWidth>
            <FormLabel>Phone Number</FormLabel>
            <TextField
              size="small"
              placeholder="+1234567890"
              error={!!errors.phone_number}
              helperText={errors.phone_number?.message}
              {...register('phone_number', { required: 'Phone number is required' })}
            />
          </FormControl>

          {/* First Name */}
          <FormControl fullWidth>
            <FormLabel>First Name</FormLabel>
            <TextField
              size="small"
              placeholder="John"
              error={!!errors.first_name}
              helperText={errors.first_name?.message}
              {...register('first_name', { required: 'First name is required' })}
            />
          </FormControl>

          {/* Last Name */}
          <FormControl fullWidth>
            <FormLabel>Last Name</FormLabel>
            <TextField
              size="small"
              placeholder="Doe"
              error={!!errors.last_name}
              helperText={errors.last_name?.message}
              {...register('last_name', { required: 'Last name is required' })}
            />
          </FormControl>

          {/* Roles (full width) */}
          <FormControl fullWidth sx={{ gridColumn: '1 / -1' }}>
            <FormLabel>Roles</FormLabel>
            <Controller
              name="role_ids"
              control={control}
              rules={{ required: 'At least one role is required' }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size="small"
                  options={roles || []}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name}
                  value={roles?.filter((r) => value?.includes(r.id)) || []}
                  onChange={(_, selectedOptions) =>
                    onChange(selectedOptions.map((option) => option.id))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select roles"
                      error={!!errors.role_ids}
                      helperText={errors.role_ids?.message}
                    />
                  )}
                />
              )}
            />
          </FormControl>

          {/* Divider */}
          <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />

          {/* Actions */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{ minWidth: 160 }}
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
